/**
 * „Heute" — die Startseite des Admins (Redesign 03.09.2026).
 *
 * Drei Spalten: Was braucht dich · Was ist neu · Was läuft + To-Dos.
 * Jede Zeile ist ein Link in den Kunden-Arbeitsbereich oder zur Anfrage.
 * Kunden landen weiterhin bei ihren Anfragen.
 */

import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowRight, CheckCircle2, Inbox, Loader2, RefreshCw, ListTodo } from 'lucide-react';
import { useAuthStore } from '@/hooks/useAuth';
import { getHeute, type HeuteEintrag } from '@/lib/analytics-api';
import { AmpelPunkt } from '@/components/Ampel';

function relativ(iso?: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 60) return `vor ${Math.max(1, min)} Min.`;
  const h = Math.round(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const t = Math.round(h / 24);
  return t === 1 ? 'gestern' : `vor ${t} Tagen`;
}

function Zeile({ e }: { e: HeuteEintrag }) {
  return (
    <Link to={e.link} className="group flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 transition">
      <span className="mt-[7px]">
        {e.stufe ? <AmpelPunkt status={e.stufe} /> : <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-medium text-gray-900" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{e.titel || e.text}</span>
        {e.titel && <span className="block text-xs text-gray-500 truncate" title={e.text}>{e.text}</span>}
        {/* Zeit in der zweiten Zeile — neben dem Text quetscht sie ihn zusammen. */}
        {!e.titel && e.zeit && <span className="block text-xs text-gray-400">{relativ(e.zeit)}</span>}
      </span>
      <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 mt-1 flex-shrink-0" />
    </Link>
  );
}

function Karte({ titel, zaehler, kinder, leer, icon }: { titel: string; zaehler?: number; kinder: React.ReactNode; leer: string; icon: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100">
        {icon}
        <h2 className="text-[13px] font-semibold text-gray-900">{titel}</h2>
        {typeof zaehler === 'number' && <span className="ml-auto text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{zaehler}</span>}
      </header>
      <div className="divide-y divide-gray-50">
        {zaehler === 0 ? <p className="px-4 py-6 text-[13px] text-gray-400 text-center">{leer}</p> : kinder}
      </div>
    </section>
  );
}

export default function HeutePage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const { data, isLoading, error, refetch, isFetching } = useQuery({ queryKey: ['heute'], queryFn: getHeute, enabled: isAdmin, staleTime: 60_000 });

  if (!isAdmin) return <Navigate to="/leads" replace />;

  const heute = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">{heute}</p>
          <h1 className="text-xl font-bold text-gray-900 mt-0.5">Heute</h1>
        </div>
        <button onClick={() => refetch()} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100" title="Aktualisieren">
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>}
      {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}

      {data && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1.1fr_1fr] gap-5 items-start">
          <Karte
            titel="Braucht dich"
            zaehler={data.braucht_dich.length}
            icon={<AlertCircle className="w-4 h-4 text-amber-500" />}
            leer="Nichts offen — alles läuft."
            kinder={data.braucht_dich.map((e, i) => <Zeile key={i} e={e} />)}
          />
          <Karte
            titel="Neu eingegangen"
            zaehler={data.neu.length}
            icon={<Inbox className="w-4 h-4 text-blue-500" />}
            leer="Seit einer Woche keine neuen Anfragen."
            kinder={data.neu.map((e, i) => <Zeile key={i} e={e} />)}
          />
          <div className="space-y-5">
            <section className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="flex items-center gap-2 text-[13px] font-semibold text-gray-900 mb-3"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Läuft</h2>
              <dl className="space-y-2 text-[13px]">
                <div className="flex justify-between gap-3"><dt className="text-gray-500">Kampagnen aktiv</dt><dd className="font-medium text-gray-900">{data.laeuft.kampagnen_aktiv}{data.laeuft.kampagnen_lernphase > 0 && <span className="text-gray-500 font-normal"> · {data.laeuft.kampagnen_lernphase} in Lernphase</span>}{data.laeuft.kampagnen_eingeschraenkt > 0 && <span className="text-amber-600"> · {data.laeuft.kampagnen_eingeschraenkt} eingeschränkt</span>}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-gray-500">Übertragung an Google (24 h)</dt><dd className="font-medium text-gray-900">{data.laeuft.uploads_ok + data.laeuft.uploads_fehler === 0 ? <span className="text-gray-500">nichts anstehend</span> : <>{data.laeuft.uploads_ok} ok{data.laeuft.uploads_fehler > 0 && <span className="text-red-600"> · {data.laeuft.uploads_fehler} Fehler</span>}</>}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-gray-500">Zielvorhaben</dt><dd className={`font-medium ${data.laeuft.zielvorhaben_ok ? 'text-gray-900' : 'text-red-600'}`}>{data.laeuft.zielvorhaben_ok ? 'geprüft' : 'Fehler'}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-gray-500">Berichte diesen Monat</dt><dd className="font-medium text-gray-900">{data.laeuft.berichte_versandt} versandt</dd></div>
              </dl>
            </section>
            <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <header className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100">
                <ListTodo className="w-4 h-4 text-gray-500" />
                <h2 className="text-[13px] font-semibold text-gray-900">To-Dos</h2>
                <Link to="/todos" className="ml-auto text-xs text-blue-600 hover:underline">alle</Link>
              </header>
              {data.todos.length === 0 ? <p className="px-4 py-5 text-[13px] text-gray-400 text-center">Keine offenen Aufgaben.</p> : (
                <ul className="divide-y divide-gray-50">
                  {data.todos.map((t) => (
                    <li key={t.id} className="px-4 py-2 text-[13px] flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.prioritaet === 'hoch' ? 'bg-red-500' : 'bg-gray-300'}`} />
                      <span className="flex-1 truncate text-gray-800">{t.titel}{t.kunde && <span className="text-gray-400"> · {t.kunde}</span>}</span>
                      {t.deadline && <span className="text-xs text-gray-400">{new Date(t.deadline).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
